/**
 * Protetor de rotas por papel de usuário (Admin, Staff, Capitão, Jogador).
 *
 * Garante que a interface responda de forma clara e elegante quando o visitante
 * ou usuário não tiver as permissões necessárias para a tela.
 */

import { type ReactNode } from 'react';
import { Link, useLocation } from 'react-router';
import { useAuth } from '@/lib/auth';
import { PageShell, Skeleton } from '@/components/ui';

const ROLE_RANK = { player: 0, captain: 1, staff: 2, admin: 3 } as const;
type RoleName = keyof typeof ROLE_RANK;

const ROLE_LABEL: Record<RoleName, string> = {
  admin: 'Administrador',
  staff: 'Equipe Staff',
  captain: 'Capitão de Equipe',
  player: 'Jogador',
};

interface RequireRoleProps {
  role: RoleName;
  children: ReactNode;
}

export function RequireRole({ role, children }: RequireRoleProps) {
  const { user, loading, isAdmin, isStaff } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <PageShell>
        <Skeleton className="h-24 w-full" />
        <Skeleton className="mt-8 h-96 w-full" />
      </PageShell>
    );
  }

  // Não autenticado
  if (!user) {
    return (
      <PageShell>
        <div className="mpc-panel mx-auto max-w-md p-8 text-center">
          <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-brand/15 text-brand-ink">
            <svg className="size-6 text-brand-ink" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
          <h2 className="mpc-display text-2xl text-paper">Identificação Necessária</h2>
          <p className="mt-2 text-sm text-muted">
            Você precisa estar conectado com uma conta autorizada para acessar esta área.
          </p>
          <div className="mt-6 flex justify-center gap-3">
            <Link
              to={`/entrar?returnTo=${encodeURIComponent(location.pathname + location.search)}`}
              className="rounded-full bg-brand px-6 py-2.5 text-sm font-medium text-black transition-colors hover:bg-brand-bright"
            >
              Entrar na minha conta
            </Link>
          </div>
        </div>
      </PageShell>
    );
  }

  const userRank = ROLE_RANK[user.role as RoleName] ?? 0;
  const requiredRank = ROLE_RANK[role];

  // Nível insuficiente
  if (userRank < requiredRank) {
    const isDemandingAdmin = role === 'admin';
    const isDemandingStaff = role === 'staff';

    return (
      <PageShell>
        <div className="mpc-panel mx-auto max-w-lg p-8 text-center border-loss/30">
          <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-loss/15 text-loss">
            <svg className="size-6 text-loss" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              <line x1="12" x2="12" y1="8" y2="12" />
              <line x1="12" x2="12.01" y1="16" y2="16" />
            </svg>
          </div>
          <h2 className="mpc-display text-2xl text-loss">Acesso Não Autorizado</h2>
          <p className="mt-2 text-sm text-muted leading-relaxed">
            {isDemandingAdmin
              ? 'Esta tela é restrita exclusivamente a Administradores da plataforma.'
              : isDemandingStaff
                ? 'Esta tela é restrita à Organização (membros da Staff e Administradores).'
                : `Esta funcionalidade requer nível de permissão de "${ROLE_LABEL[role]}".`}
          </p>
          <div className="mt-4 rounded-lg bg-surface-2 p-3 text-xs text-faint">
            Seu nível atual: <strong className="text-paper">{ROLE_LABEL[user.role as RoleName] ?? user.role}</strong>
          </div>
          <div className="mt-6 flex justify-center gap-3">
            <Link
              to="/"
              className="rounded-full border border-line-strong px-5 py-2 text-sm font-medium transition-colors hover:border-brand/50"
            >
              Página inicial
            </Link>
            {isStaff && (
              <Link
                to="/admin"
                className="rounded-full bg-brand px-5 py-2 text-sm font-medium text-black transition-colors hover:bg-brand-bright"
              >
                Painel da Organização
              </Link>
            )}
          </div>
        </div>
      </PageShell>
    );
  }

  return <>{children}</>;
}
